import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Award, GraduationCap, Briefcase } from 'lucide-react';
import ConsultationDialog from '../components/ConsultationDialog';

const OurAttorneys = () => {
  const [isConsultationOpen, setIsConsultationOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const attorneys = [
    {
      name: 'Elizabeth Morrison',
      title: 'Managing Partner',
      specialization: 'Corporate Law & M&A',
      education: 'Harvard Law School, J.D.',
      experience: '25 years',
      description: 'Elizabeth specializes in complex corporate transactions, mergers and acquisitions, and business formation. She has successfully guided hundreds of companies through critical legal matters.',
      expertise: ['Corporate Law', 'Mergers & Acquisitions', 'Business Formation', 'Contract Negotiation'],
    },
    {
      name: 'David Chen',
      title: 'Senior Partner',
      specialization: 'Intellectual Property',
      education: 'Stanford Law School, J.D.',
      experience: '20 years',
      description: 'David is a leading expert in intellectual property law, with extensive experience in trademark, copyright, and patent matters. He helps businesses protect their most valuable assets.',
      expertise: ['Trademark Law', 'Copyright Law', 'Patent Prosecution', 'IP Litigation'],
    },
    {
      name: 'Sarah Williams',
      title: 'Partner',
      specialization: 'Immigration Law',
      education: 'UC Berkeley School of Law, J.D.',
      experience: '15 years',
      description: 'Sarah provides comprehensive immigration services for individuals and businesses, including visa applications, green cards, and citizenship matters.',
      expertise: ['Immigration', 'Visa Applications', 'Green Cards', 'Citizenship'],
    },
    {
      name: 'Michael Rodriguez',
      title: 'Partner',
      specialization: 'Business Litigation',
      education: 'Columbia Law School, J.D.',
      experience: '18 years',
      description: 'Michael is a seasoned litigator who represents clients in complex business disputes, contract disagreements, and commercial litigation matters.',
      expertise: ['Business Litigation', 'Contract Disputes', 'Commercial Law', 'Arbitration'],
    },
    {
      name: 'Jennifer Park',
      title: 'Partner',
      specialization: 'Corporate Compliance',
      education: 'Yale Law School, J.D.',
      experience: '16 years',
      description: 'Jennifer helps businesses navigate complex regulatory environments, ensuring compliance with anti-corruption laws and government contracting requirements.',
      expertise: ['Corporate Compliance', 'Anti-Corruption', 'Government Contracting', 'Regulatory Affairs'],
    },
    {
      name: 'Robert Thompson',
      title: 'Partner',
      specialization: 'Non-Profit Law',
      education: 'Georgetown Law, J.D.',
      experience: '14 years',
      description: 'Robert specializes in non-profit formation, tax-exempt status, and governance matters, helping organizations achieve their charitable missions.',
      expertise: ['Non-Profit Law', 'Tax-Exempt Organizations', 'Charitable Governance', 'Grant Compliance'],
    },
  ];

  const paralegals = [
    {
      name: 'Amanda Martinez',
      title: 'Senior Paralegal',
      specialization: 'Corporate Law',
      education: 'Paralegal Certificate, UCLA Extension',
      experience: '12 years',
      description: 'Amanda provides essential support for corporate transactions, contract management, and business formation matters with exceptional attention to detail.',
      expertise: ['Contract Review', 'Corporate Filings', 'Due Diligence', 'Document Management'],
    },
    {
      name: 'James Foster',
      title: 'Senior Paralegal',
      specialization: 'Litigation Support',
      education: 'Paralegal Studies, San Francisco State',
      experience: '10 years',
      description: 'James excels in litigation support, managing discovery, trial preparation, and client communication for complex business disputes.',
      expertise: ['Discovery Management', 'Trial Preparation', 'Legal Research', 'Case Management'],
    },
    {
      name: 'Lisa Chang',
      title: 'Paralegal',
      specialization: 'Intellectual Property',
      education: 'Paralegal Certificate, Berkeley City College',
      experience: '8 years',
      description: 'Lisa assists with trademark and copyright registrations, IP portfolio management, and compliance documentation.',
      expertise: ['Trademark Filing', 'Copyright Registration', 'IP Research', 'Portfolio Management'],
    },
    {
      name: 'Daniel Washington',
      title: 'Paralegal',
      specialization: 'Immigration',
      education: 'Paralegal Studies, Community College of SF',
      experience: '7 years',
      description: 'Daniel supports immigration cases by preparing visa applications, maintaining case files, and coordinating with government agencies.',
      expertise: ['Visa Applications', 'Form Preparation', 'Case Tracking', 'Client Coordination'],
    },
    {
      name: 'Rebecca Johnson',
      title: 'Paralegal',
      specialization: 'Compliance',
      education: 'Paralegal Certificate, Stanford Continuing Studies',
      experience: '6 years',
      description: 'Rebecca assists with compliance audits, regulatory filings, and maintaining comprehensive compliance documentation systems.',
      expertise: ['Compliance Audits', 'Regulatory Filings', 'Policy Documentation', 'Risk Assessment'],
    },
    {
      name: 'Christopher Lee',
      title: 'Paralegal',
      specialization: 'Non-Profit & Contracts',
      education: 'Paralegal Studies, City College of San Francisco',
      experience: '5 years',
      description: 'Christopher provides support for non-profit matters and contract administration, ensuring smooth operations and compliance.',
      expertise: ['Non-Profit Filings', 'Contract Administration', 'Grant Documentation', 'Board Governance'],
    },
  ];

  return (
    <div data-testid="our-attorneys-page">
      <Helmet>
        <title>Our Attorneys & Legal Team - Zorvian Associates</title>
        <meta name="description" content="Meet our team of experienced attorneys and paralegals at Zorvian Associates. Our legal professionals specialize in corporate law, intellectual property, immigration, business litigation, and more." />
        <meta name="keywords" content="attorneys, lawyers, legal team, corporate attorney, IP lawyer, immigration attorney, business litigation, paralegals, legal professionals" />
      </Helmet>
      
      {/* Enhanced Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-heading transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Our Attorneys
            </h1>
            <p className={`text-lg text-slate-300 max-w-2xl mx-auto transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Meet our team of experienced legal professionals dedicated to protecting your interests and achieving exceptional results.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys Grid */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Our Attorneys</h2>
            <p className="text-lg text-slate-600">
              Experienced legal professionals dedicated to your success
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {attorneys.map((attorney, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-1 font-heading">{attorney.name}</h3>
                    <p className="text-sm text-[#2563EB] font-semibold mb-2">{attorney.title}</p>
                    <Badge className="bg-blue-50 text-[#2563EB] hover:bg-blue-100">
                      {attorney.specialization}
                    </Badge>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start space-x-2 text-sm">
                      <GraduationCap className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{attorney.education}</span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm">
                      <Briefcase className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{attorney.experience} of experience</span>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 mb-4">{attorney.description}</p>

                  <div>
                    <p className="text-xs font-semibold text-[#0F172A] mb-2">Areas of Expertise:</p>
                    <div className="flex flex-wrap gap-2">
                      {attorney.expertise.map((skill, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Paralegals Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Our Paralegals</h2>
            <p className="text-lg text-slate-600">
              Skilled legal professionals providing exceptional support
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paralegals.map((paralegal, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <CardContent className="p-6">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-bold text-[#0F172A] mb-1 font-heading">{paralegal.name}</h3>
                    <p className="text-sm text-[#64748B] font-semibold mb-2">{paralegal.title}</p>
                    <Badge className="bg-slate-50 text-[#64748B] hover:bg-slate-100">
                      {paralegal.specialization}
                    </Badge>
                  </div>

                  <div className="space-y-3 mb-4">
                    <div className="flex items-start space-x-2 text-sm">
                      <GraduationCap className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{paralegal.education}</span>
                    </div>
                    <div className="flex items-start space-x-2 text-sm">
                      <Briefcase className="h-4 w-4 text-slate-400 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{paralegal.experience} of experience</span>
                    </div>
                  </div>

                  <p className="text-sm text-slate-600 mb-4">{paralegal.description}</p>

                  <div>
                    <p className="text-xs font-semibold text-[#0F172A] mb-2">Areas of Expertise:</p>
                    <div className="flex flex-wrap gap-2">
                      {paralegal.expertise.map((skill, i) => (
                        <Badge key={i} variant="outline" className="text-xs">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0F172A] mb-4 font-heading">Credentials & Recognition</h2>
              <p className="text-lg text-slate-600">
                Our attorneys are recognized for their excellence and expertise
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-[#B45309]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">AV Preeminent Rated</h3>
                <p className="text-sm text-slate-600">Highest peer rating for legal ability and ethical standards</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-[#B45309]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">Super Lawyers</h3>
                <p className="text-sm text-slate-600">Recognized by peers as top attorneys in their fields</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-xl transition-all duration-300 hover:-translate-y-2">
                <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-[#B45309]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-2">Bar Certified</h3>
                <p className="text-sm text-slate-600">Licensed to practice in all 50 states</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-br from-[#0F172A] via-[#1e293b] to-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
          }}></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6 font-heading">Work With Our Expert Team</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-300">
            Get matched with the right attorney for your specific legal needs.
          </p>
          <Button
            onClick={() => setIsConsultationOpen(true)}
            size="lg"
            className="bg-[#2563EB] hover:bg-[#1d4ed8] text-white px-8 py-6 text-lg shadow-xl shadow-blue-500/20"
            data-testid="attorneys-cta-button"
          >
            Schedule Your Consultation
          </Button>
        </div>
      </section>

      <ConsultationDialog open={isConsultationOpen} onOpenChange={setIsConsultationOpen} />
    </div>
  );
};

export default OurAttorneys;
